import { UserCheck, CheckCircle2, Ban, Hand } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CHIP = { open: "text-cyan-300", assigned: "text-emerald-300", done: "text-slate-400", cancelled: "text-slate-500" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

export function AdminJobList({ jobs, onChanged }) {
  if (!jobs.length) return <p className="text-sm text-slate-500" data-testid="jobs-empty">No jobs posted yet.</p>;

  const assign = async (j, email) => {
    try { await api.post(`/admin/jobs/${j.job_id}/assign`, { tech_email: email }); toast.success("Assigned — tech notified, others told it's taken"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't assign"); }
  };
  const setStatus = async (j, status) => {
    if (status === "cancelled" && !window.confirm(`Cancel ${j.job_id}? Techs will see it disappear.`)) return;
    try { await api.put(`/admin/jobs/${j.job_id}`, { status }); toast.success(status === "done" ? "Marked done" : "Cancelled"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-jobs-list">
      {jobs.map((j) => (
        <div key={j.job_id} className="dg-card rounded-xl p-4" data-testid={`admin-job-${j.job_id}`}>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className={`dg-chip ${CHIP[j.status] || ""}`} data-testid={`admin-job-status-${j.job_id}`}>{j.status}</span>
            <span className="font-semibold text-slate-100">{j.title}</span>
            <span className="text-sm text-slate-400">{j.area}{j.date_window ? ` · ${j.date_window}` : ""}{j.pay_usd > 0 ? ` · $${Number(j.pay_usd).toFixed(2)}` : ""}</span>
            <span className="text-xs text-slate-500 font-mono ml-auto">{j.job_id} · {fmt(j.created_at)}{j.notified_count != null ? ` · emailed ${j.notified_count} tech${j.notified_count === 1 ? "" : "s"}` : ""}</span>
          </div>
          {j.customer?.name && <div className="text-xs text-slate-400 mt-1">Customer: {j.customer.name}{j.customer.phone ? ` · ${j.customer.phone}` : ""}{j.customer.address ? ` · ${j.customer.address}` : ""}{j.ticket_id ? ` · ${j.ticket_id}` : ""}</div>}
          {j.assigned && <div className="text-sm text-emerald-300 mt-2 inline-flex items-center gap-1" data-testid={`admin-job-assigned-${j.job_id}`}><UserCheck className="w-4 h-4" /> {j.assigned.tech_name} · {j.assigned.tech_email} · {fmt(j.assigned.at)}</div>}
          {j.status === "open" && (
            <div className="mt-3" data-testid={`admin-job-interests-${j.job_id}`}>
              {(j.interests || []).length === 0 ? <div className="text-xs text-slate-500 inline-flex items-center gap-1"><Hand className="w-3 h-3" /> No hands raised yet</div> : (
                <ul className="space-y-2">
                  {j.interests.map((i) => (
                    <li key={i.tech_email} className="flex flex-wrap items-center gap-2 text-sm">
                      <Hand className="w-4 h-4 text-[#FFD600]" />
                      <span className="text-slate-100">{i.tech_name}</span><span className="text-slate-400">{i.tech_email}</span>
                      {i.note && <span className="text-slate-300 italic">“{i.note}”</span>}
                      <button type="button" onClick={() => assign(j, i.tech_email)} className="dg-btn-primary rounded-full px-3 py-1 text-xs inline-flex items-center gap-1" data-testid={`admin-job-assign-${j.job_id}-${i.tech_email.replace(/[^a-z0-9]/gi, "_")}`}><UserCheck className="w-3 h-3" /> Assign</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-3 text-xs">
            {j.status === "assigned" && <button type="button" onClick={() => setStatus(j, "done")} className="dg-link inline-flex items-center gap-1" data-testid={`admin-job-done-${j.job_id}`}><CheckCircle2 className="w-3 h-3" /> Mark done</button>}
            {(j.status === "open" || j.status === "assigned") && <button type="button" onClick={() => setStatus(j, "cancelled")} className="text-rose-300 hover:text-rose-200 inline-flex items-center gap-1" data-testid={`admin-job-cancel-${j.job_id}`}><Ban className="w-3 h-3" /> Cancel</button>}
          </div>
        </div>
      ))}
    </div>
  );
}
